import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from 'store/reducers';

import {
    type AuthChallenge,
    type AuthClient,
    type AuthErrorCode,
    type AuthUser,
    type CompleteSignInInput,
    type LoginCredentials,
    type SignInResult,
    toAuthError,
} from './AuthClient';

export type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';
type RequestStatus = 'idle' | 'pending';
type AuthNotification = {
    type: 'success' | 'error';
    message: string;
};

export type AuthState = {
    status: AuthStatus;
    user: AuthUser | null;
    restoreRequestId: string | null;
    loginStatus: RequestStatus;
    logoutStatus: RequestStatus;
    /** Set when the provider requires another sign in step, such as an MFA code. */
    pendingChallenge: AuthChallenge | null;
    notification: AuthNotification | null;
};

type AuthThunkConfig = {
    extra: AuthClient;
    rejectValue: AuthErrorCode;
};

/**
 * The one place that turns a provider independent error code into display copy. A new provider
 * only has to report the right code; wording stays here.
 */
const signInErrorMessages: Record<AuthErrorCode, string> = {
    invalidCredentials: 'Unknown email or password. Please try again.',
    sessionExpired: 'Your session expired. Please sign in again.',
    network: 'No connection to the sign in service. Please check your network.',
    unavailable: 'The sign in service is currently unavailable. Please try again later.',
    notSupported: 'This sign in method is not available.',
    unknown: 'Sign in failed. Please try again.',
};

const signOutErrorMessages: Record<AuthErrorCode, string> = {
    invalidCredentials: 'Sign out failed. Please try again.',
    sessionExpired: 'Your session already expired.',
    network: 'No connection to the sign in service. Please check your network.',
    unavailable: 'The sign in service is currently unavailable. Please try again later.',
    notSupported: 'Sign out is not available.',
    unknown: 'Sign out failed. Please try again.',
};

export const createAuthState = (user: AuthUser | null, status?: AuthStatus): AuthState => ({
    status: status ?? (user === null ? 'unauthenticated' : 'authenticated'),
    user,
    restoreRequestId: null,
    loginStatus: 'idle',
    logoutStatus: 'idle',
    pendingChallenge: null,
    notification: null,
});

const initialState = createAuthState(null, 'loading');

export const restoreSession = createAsyncThunk<AuthUser | null, void, { extra: AuthClient }>(
    'auth/restoreSession',
    async (_argument, { extra }) => extra.getCurrentUser()
);

export const signIn = createAsyncThunk<SignInResult, LoginCredentials | undefined, AuthThunkConfig>(
    'auth/signIn',
    async (credentials, { extra, rejectWithValue }) => {
        try {
            return await extra.signIn(credentials);
        } catch (error) {
            return rejectWithValue(toAuthError(error).code);
        }
    }
);

export const completeSignIn = createAsyncThunk<SignInResult, CompleteSignInInput, AuthThunkConfig>(
    'auth/completeSignIn',
    async (input, { extra, rejectWithValue }) => {
        if (extra.completeSignIn === undefined) {
            return rejectWithValue('notSupported');
        }

        try {
            return await extra.completeSignIn(input);
        } catch (error) {
            return rejectWithValue(toAuthError(error).code);
        }
    }
);

export const signOut = createAsyncThunk<void, void, AuthThunkConfig>(
    'auth/signOut',
    async (_argument, { extra, rejectWithValue }) => {
        try {
            await extra.signOut();
        } catch (error) {
            return rejectWithValue(toAuthError(error).code);
        }
    }
);

const applySignInResult = (state: AuthState, result: SignInResult): AuthState => {
    if (result.status === 'authenticated') {
        return {
            ...createAuthState(result.user),
            notification: { type: 'success', message: 'Signed in successfully.' },
        };
    }

    if (result.status === 'challenge') {
        return { ...state, loginStatus: 'idle', pendingChallenge: result.challenge };
    }

    return { ...state, loginStatus: 'pending', pendingChallenge: null };
};

const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        authStateChanged: (state, action: PayloadAction<AuthUser | null>) => ({
            ...createAuthState(action.payload),
            notification: state.notification,
        }),
        authNotificationConsumed: state => {
            state.notification = null;
        },
    },
    extraReducers: builder => {
        builder
            .addCase(restoreSession.pending, (state, action) => {
                state.status = 'loading';
                state.restoreRequestId = action.meta.requestId;
            })
            .addCase(restoreSession.fulfilled, (state, action) => {
                if (state.restoreRequestId === action.meta.requestId) {
                    return createAuthState(action.payload);
                }
            })
            .addCase(restoreSession.rejected, (state, action) => {
                if (state.restoreRequestId === action.meta.requestId) {
                    return createAuthState(null);
                }
            })
            .addCase(signOut.pending, state => {
                state.restoreRequestId = null;
                state.logoutStatus = 'pending';
            })
            .addCase(signOut.fulfilled, () => createAuthState(null))
            .addCase(signOut.rejected, (state, action) => {
                state.logoutStatus = 'idle';
                state.notification = {
                    type: 'error',
                    message: signOutErrorMessages[action.payload ?? 'unknown'],
                };
            })
            .addMatcher(
                action => signIn.pending.match(action) || completeSignIn.pending.match(action),
                state => {
                    state.restoreRequestId = null;
                    state.loginStatus = 'pending';
                }
            )
            .addMatcher(
                action => signIn.fulfilled.match(action) || completeSignIn.fulfilled.match(action),
                (state, action: PayloadAction<SignInResult>) => applySignInResult(state, action.payload)
            )
            .addMatcher(
                action => signIn.rejected.match(action) || completeSignIn.rejected.match(action),
                (state, action) => {
                    const code = (action.payload as AuthErrorCode | undefined) ?? 'unknown';
                    state.loginStatus = 'idle';
                    state.notification = { type: 'error', message: signInErrorMessages[code] };
                }
            );
    },
});

export const { authNotificationConsumed, authStateChanged } = authSlice.actions;

export const selectAuthStatus = (state: RootState) => state.auth.status;
export const selectCurrentUser = (state: RootState) => state.auth.user;
export const selectIsSigningIn = (state: RootState) => state.auth.loginStatus === 'pending';
export const selectIsSigningOut = (state: RootState) => state.auth.logoutStatus === 'pending';
export const selectPendingChallenge = (state: RootState) => state.auth.pendingChallenge;
export const selectAuthNotification = (state: RootState) => state.auth.notification;

export default authSlice.reducer;
